import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  DEFAULT_DIMENSION,
  DIMENSIONS,
  MARKET_SHARE_VIEW_TYPE_DATA,
  PIVOT_NAMES,
} from "../../constants";

import { changeMarketShareSettings, checkSellerDataPartial } from "../../actions";
import PivotDropdown from "./pivot-dropdown/pivot-dropdown";
import { ROOTDOMAINS } from "../../../../utils/other/constants";
import { analyticsPivotSelected } from "../../../shared/analytics/actions";

function MarketShareDataFilter({
  changeMarketShareSettings,
  checkSellerDataPartial,
  productAttributes,
  settings,
  viewDefinitions,
  partialData,
  selectedDateRange,
  selectedPreviousDateRange,
  currentViewConfigQuery,
}) {
  const setDefaultDimensionSettings = () => {
    changeMarketShareSettings({
      ...settings,
      dimension: DEFAULT_DIMENSION,
      dimensionPivot: null,
    });
  };

  useEffect(() => {
    if (settings.dimension === undefined) {
      setDefaultDimensionSettings();
    }
  }, [settings]);

  const { dimension, dimensionPivot } = settings;

  useEffect(() => {
    if (dimension !== undefined) {
      analyticsPivotSelected(
        MARKET_SHARE_VIEW_TYPE_DATA.id,
        PIVOT_NAMES[dimension],
        dimensionPivot
      );
    }
  }, [dimension, dimensionPivot]);

  const onPivotSelect = (d, dp) => {
    changeMarketShareSettings({
      dimension: d,
      dimensionPivot: dp,
    });
  };

  const attributes = useMemo(() => {
    if (!productAttributes || !viewDefinitions) return [];

    const allCategoryFilters = productAttributes.filter(
      (x) => x.mbName == "mb_category"
    );
    const selectedCategories = [];
    for (const categoryFilter of allCategoryFilters)
      selectedCategories.push(...categoryFilter.values);

    const retailers = productAttributes.find(
      (x) => x.mbName == "rootdomain.keyword"
    )?.values;

    const attrs = viewDefinitions?.filterDefinitions?.find(
      (x) => x.mbName == "attributes"
    )?.values;

    if (!retailers || !attrs) return [];

    const relevantAttrs = [];
    for (const category of selectedCategories) {
      const catAtrPerRoot = [];
      for (const retailer of retailers) {
        if (attrs[ROOTDOMAINS[retailer]]?.[category]) {
          catAtrPerRoot.push(
            Object.keys(attrs[ROOTDOMAINS[retailer]][category])
          );
        }
      }
      if (catAtrPerRoot.length > 0) {
        relevantAttrs.push(
          ...catAtrPerRoot.reduce((a, b) => a.filter((c) => b.includes(c)))
        );
      }
    }
    relevantAttrs.sort((a, b) => a.localeCompare(b)); // sort alphabetically
    return [...new Set(relevantAttrs)]; // removes duplicates
  }, [viewDefinitions, productAttributes]);

  useEffect(() => {
    if (dimensionPivot && !attributes.includes(dimensionPivot))
      setDefaultDimensionSettings();
  }, [attributes, dimensionPivot]);

  // Check for partial data on page load and when filters change
  useEffect(() => {
    if (currentViewConfigQuery && selectedDateRange && selectedPreviousDateRange) {
      const requestParams = {
        insightViews: currentViewConfigQuery,
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate,
        previousStartDate: selectedPreviousDateRange.previousStartDate,
        previousEndDate: selectedPreviousDateRange.previousEndDate,
      };
      checkSellerDataPartial(requestParams);
    }
  }, [
    currentViewConfigQuery,
    selectedDateRange,
    selectedPreviousDateRange,
    checkSellerDataPartial,
  ]);

  // If partial data and current dimension is Sellers, switch to default
  useEffect(() => {
    if (partialData && dimension === DIMENSIONS.sellers) {
      changeMarketShareSettings({
        ...settings,
        dimension: DEFAULT_DIMENSION,
        dimensionPivot: null,
      });
    }
  }, [partialData, dimension, settings, changeMarketShareSettings]);

  return (
    <PivotDropdown
      selected={{
        main: dimension,
        sub: dimensionPivot,
      }}
      attributes={attributes}
      onSelect={onPivotSelect}
      filterDefinitions={viewDefinitions?.filterDefinitions}
      partialData={partialData}
    />
  );
}

export const mapStateToProps = (state) => {
  return {
    viewDefinitions: state.marketShare.insights.insights.viewDefinitions,
    productAttributes:
      state.marketShare.insights.insights.currentViewConfig.query
        .productAttributes,
    settings:
      state.marketShare.insights.insights.currentViewConfig.query.settings,
    partialData: state.marketShare.marketShare.tableStatus.partial,
    currentViewConfigQuery:
      state.marketShare.insights.insights.currentViewConfig.query,
    selectedDateRange:
      state.marketShare.insights.mbFilterRow.selectedDateRange,
    selectedPreviousDateRange:
      state.marketShare.insights.mbFilterRow.selectedPreviousDateRange,
  };
};

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { changeMarketShareSettings, checkSellerDataPartial },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MarketShareDataFilter);
